import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { ShieldCheck, Copy, Check, KeyRound } from 'lucide-react';
import {
  authKeys,
  useMfaEnable,
  useMfaSetup,
  type MfaEnableData,
  type MfaSetupData,
} from '../../lib/api/queries';

type Phase =
  | { kind: 'loading-setup' }
  | { kind: 'verify';        setup: MfaSetupData; qrDataUrl: string }
  | { kind: 'recovery-codes'; enable: MfaEnableData };

/**
 * Mandatory MFA enrollment landing page. Shown to any signed-in user whose
 * /me reports {@code mfaEnabled: false}. Once enrollment completes and the
 * user confirms they've saved their recovery codes, this page promotes the
 * cached user in the query client so the App.tsx gate flips to the main app.
 */
export function MfaSetupPage() {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading-setup' });
  const [code,  setCode]  = useState('');

  const setup  = useMfaSetup();
  const enable = useMfaEnable();

  // Kick off setup on mount. Each mount resets the authenticator key on the
  // server (intentional — a stale half-completed key from a previous session
  // shouldn't be reusable), so we only want to fire this once. React 18's
  // StrictMode runs effects twice in dev which would scan a QR the user
  // can't use; the early-return guard fixes that.
  useEffect(() => {
    if (setup.isPending || setup.data || setup.isError) return;
    setup.mutate(undefined, {
      onSuccess: async (data) => {
        try {
          const dataUrl = await QRCode.toDataURL(data.authenticatorUri, {
            margin: 1,
            width: 220,
          });
          setPhase({ kind: 'verify', setup: data, qrDataUrl: dataUrl });
        } catch (err) {
          console.error('Failed to render QR code', err);
          // Fall back to manual-key entry — phase still 'verify', with
          // an empty data URL so the <img> simply doesn't render.
          setPhase({ kind: 'verify', setup: data, qrDataUrl: '' });
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onVerifySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    enable.mutate(
      { code },
      {
        onSuccess: (data) => {
          setPhase({ kind: 'recovery-codes', enable: data });
        },
      },
    );
  };

  const onAcknowledgeRecoveryCodes = () => {
    if (phase.kind !== 'recovery-codes') return;
    // Promote the user to "enrolled" in the cache. App.tsx's gate watches
    // useCurrentUser and will swap MfaSetupPage out for the main app on
    // this re-render.
    qc.setQueryData(authKeys.me, phase.enable.user);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-6">
          <ShieldCheck className="w-8 h-8 text-brand-600" aria-hidden />
          <span className="text-2xl font-semibold text-brand-700">DpasSuite</span>
        </div>

        {phase.kind === 'loading-setup' && (
          <Card>
            <p className="text-sm text-gray-600">Preparing your authenticator setup…</p>
            {setup.isError && (
              <p role="alert" className="mt-2 text-sm text-danger-500">
                Couldn't start MFA setup. Refresh the page to try again.
              </p>
            )}
          </Card>
        )}

        {phase.kind === 'verify' && (
          <VerifyCard
            setup={phase.setup}
            qrDataUrl={phase.qrDataUrl}
            code={code} setCode={setCode}
            onSubmit={onVerifySubmit}
            isPending={enable.isPending}
            errorMessage={enableErrorMessage(enable.error, enable.isError)}
          />
        )}

        {phase.kind === 'recovery-codes' && (
          <RecoveryCodesCard
            codes={phase.enable.recoveryCodes}
            onContinue={onAcknowledgeRecoveryCodes}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
      {children}
    </div>
  );
}

type VerifyCardProps = {
  setup: MfaSetupData;
  qrDataUrl: string;
  code: string; setCode: (s: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  isPending: boolean;
  errorMessage: string | null;
};

function VerifyCard(p: VerifyCardProps) {
  return (
    <form onSubmit={p.onSubmit}
      className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
      <h1 className="text-lg font-semibold text-gray-900">Set up two-factor authentication</h1>
      <p className="text-sm text-gray-600">
        Two-factor authentication is required on every DpasSuite account.
        Scan the QR code with an authenticator app — Google Authenticator,
        1Password, Authy, anything that supports TOTP — then enter the
        6-digit code it shows.
      </p>

      <div className="flex flex-col items-center gap-3 py-2">
        {p.qrDataUrl
          ? <img src={p.qrDataUrl} alt="Scan this QR with your authenticator app"
                  className="rounded border border-gray-200" />
          : <p className="text-xs text-gray-500">
              QR rendering failed — use the manual key below.
            </p>}
        <details className="w-full">
          <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
            Can't scan? Enter this key manually
          </summary>
          <p className="mt-2 font-mono text-sm text-gray-800 break-all
                        bg-gray-50 border border-gray-200 rounded p-2 text-center">
            {p.setup.sharedKey}
          </p>
        </details>
      </div>

      <label className="block">
        <span className="text-sm font-medium text-gray-700">Verification code</span>
        <input
          type="text" inputMode="numeric" autoComplete="one-time-code" required autoFocus
          pattern="[0-9 ]*" maxLength={9}
          value={p.code} onChange={e => p.setCode(e.target.value)}
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md tracking-widest
                     font-mono text-lg
                     focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
        />
      </label>

      {p.errorMessage && (
        <p role="alert" className="text-sm text-danger-500">{p.errorMessage}</p>
      )}

      <button type="submit" disabled={p.isPending}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2
                   bg-brand-600 text-white rounded-md hover:bg-brand-700
                   disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
        <ShieldCheck className="w-4 h-4" aria-hidden />
        {p.isPending ? 'Verifying…' : 'Verify and enable'}
      </button>
    </form>
  );
}

function RecoveryCodesCard({ codes, onContinue }: { codes: string[]; onContinue: () => void }) {
  const [copied,        setCopied]        = useState(false);
  const [acknowledged,  setAcknowledged]  = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('Clipboard write failed', err);
    }
  };

  return (
    <Card>
      <div className="flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-brand-600" aria-hidden />
        <h1 className="text-lg font-semibold text-gray-900">Save your recovery codes</h1>
      </div>

      <p className="text-sm text-gray-600">
        Each code lets you sign in once if you lose access to your authenticator
        app. <strong>Store them somewhere safe</strong> — a password manager or
        a printed copy. They will not be shown again.
      </p>

      <ul className="grid grid-cols-2 gap-2 font-mono text-sm text-gray-800
                     bg-gray-50 border border-gray-200 rounded p-3">
        {codes.map(c => (
          <li key={c} className="text-center select-all">{c}</li>
        ))}
      </ul>

      <button type="button" onClick={onCopy}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2
                   border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50
                   transition-colors">
        {copied
          ? <><Check className="w-4 h-4 text-success-500" aria-hidden /> Copied</>
          : <><Copy className="w-4 h-4" aria-hidden /> Copy all to clipboard</>}
      </button>

      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={acknowledged}
          onChange={e => setAcknowledged(e.target.checked)}
          className="mt-0.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
        I've saved these recovery codes somewhere safe.
      </label>

      <button type="button" onClick={onContinue} disabled={!acknowledged}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2
                   bg-brand-600 text-white rounded-md hover:bg-brand-700
                   disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
        Continue to DpasSuite
      </button>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Error mapping. The enable endpoint returns 400 with a string body on a bad
// code; surface that. Otherwise generic fallback.
// ---------------------------------------------------------------------------

function enableErrorMessage(err: unknown, isError: boolean): string | null {
  if (!isError) return null;
  const status = (err as { status?: number })?.status;
  const body   = (err as { body?: unknown })?.body;
  if (status === 400 && typeof body === 'string') return body;
  if (status === 400) return 'Enter the code from your authenticator app.';
  if (status === 401) return 'Session expired — please sign in again.';
  return 'Couldn\'t enable MFA. Try again.';
}
